import { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { MdErrorOutline, MdInfoOutline, MdWarningAmber } from "react-icons/md";
import { ReactComponent as CloseIcon } from "../../assets/icons/close.svg";
import { useTranslation } from "react-i18next";
import "./NotificationCenter.css";

let idCounter = 1;

const Icon = ({ type }) => {
  switch (type) {
    case "warning":
      return <MdWarningAmber size={18} />;
    case "error":
      return <MdErrorOutline size={18} />;
    default:
      return <MdInfoOutline size={18} />;
  }
};

const Notification = ({ n, onClose }) => {
  const { t } = useTranslation();
  const {
    id,
    message,
    messageKey,
    messageParams,
    type = "info",
    actionLabel,
    action,
  } = n;

  // Translate message if messageKey is provided
  const displayMessage = messageKey
    ? t(messageKey, messageParams || {})
    : message;

  useEffect(() => {
    if (!n.sticky) {
      const timeoutId = setTimeout(() => onClose(id), n.duration || 2500);
      return () => clearTimeout(timeoutId);
    }
  }, [id, n, onClose]);

  return (
    <div className={`toast toast-${type}`} role="alert" aria-live="polite">
      <div className="toast-icon" aria-hidden="true">
        <Icon type={type} />
      </div>
      <div className="toast-message">{displayMessage}</div>
      {actionLabel && (
        <button
          className="toast-action"
          onClick={() => {
            action && action();
            onClose(id);
          }}
        >
          {actionLabel}
        </button>
      )}
      <button
        className="toast-close"
        aria-label={t("common.close")}
        onClick={() => onClose(id)}
      >
        <CloseIcon width={14} height={14} aria-hidden="true" />
      </button>
    </div>
  );
};

const NotificationCenter = () => {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const payload = (e && e.detail) || {};
      const next = {
        id: idCounter++,
        message: payload.message || "",
        messageKey: payload.messageKey || undefined,
        messageParams: payload.messageParams || {},
        type: payload.type || "info",
        duration: payload.duration || 2500,
        sticky: payload.sticky || false,
        actionLabel: payload.actionLabel,
        action: payload.action,
      };
      // Show only one toast at a time
      setToasts([next]);
    };

    window.addEventListener("app:notification", handler);
    return () => window.removeEventListener("app:notification", handler);
  }, []);

  const handleClose = (id) => {
    setToasts((s) => s.filter((t) => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return ReactDOM.createPortal(
    <div className="notification-center" aria-live="polite" aria-atomic="true">
      {toasts.map((n) => (
        <Notification key={n.id} n={n} onClose={handleClose} />
      ))}
    </div>,
    document.body,
  );
};

export default NotificationCenter;
